import React from "react";
import moment from "moment/moment";
import {
  FiActivity,
  FiAlertCircle,
  FiArrowDownRight,
  FiArrowRight,
  FiArrowUpRight,
  FiCalendar,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiFileText,
  FiFlag,
  FiHelpCircle,
  FiMessageCircle,
  FiSend,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiX,
} from "react-icons/fi";
import Layout from "../../components/layouts/Layout";
import {
  downloadRoiReport,
  fetchRoiDashboard,
  fetchRoiDefinitions,
  fetchRoiDrillDown,
  previewRoiReport,
} from "./roiDashboardApi";
import "./roi-dashboard.css";

const EMPTY_PAGINATION = { current_page: 1, last_page: 1, per_page: 20, total: 0, from: null, to: null };

const EXECUTIVE_CARDS = [
  { key: "events_activated", label: "Events activated", metric: "events_activated", icon: FiCalendar, tone: "gold" },
  { key: "media_captures", label: "Media captures", metric: "media_captures", icon: FiActivity, tone: "blue" },
  { key: "unique_guests", label: "Unique guests", metric: "unique_guests", icon: FiUsers, tone: "green" },
  { key: "follow_ups_sent", label: "Follow-ups sent", metric: "sms_sent", icon: FiSend, tone: "purple" },
  { key: "unique_replying_guests", label: "Replying guests", metric: "replies", icon: FiMessageCircle, tone: "cyan" },
  { key: "commercial_opportunities", label: "Commercial opportunities", metric: "opportunities", icon: FiTarget, tone: "orange" },
];

const GUEST_METRICS = [
  ["events_scheduled", "Events scheduled"], ["events_activated", "Events activated", "events_activated"],
  ["media_captures", "Media captures", "media_captures"], ["capture_records", "Capture records"],
  ["unique_guests", "Unique guests", "unique_guests"], ["new_contacts", "New contacts", "new_contacts"],
  ["returning_guests", "Returning guests"], ["mobile_numbers_captured", "Mobile numbers"],
  ["email_addresses_captured", "Email addresses"], ["contactable_capture_rate", "Contactable capture rate", null, "percent"],
  ["guest_opt_in_rate", "Guest opt-in rate", null, "percent"], ["average_leads_per_activated_event", "Avg. leads per activated event"],
];

const FOLLOW_UP_METRICS = [
  ["scheduled", "Scheduled", "sms_scheduled"], ["sent", "Sent", "sms_sent"], ["delivered", "Delivered", "sms_delivered"],
  ["delivery_rate", "Delivery rate", null, "percent"], ["failed", "Failed", "sms_failed"], ["queued", "Queued"],
  ["links_sent", "Links sent"], ["link_clicks", "Link click events", "link_clicks"], ["links_clicked", "Links clicked"], ["unique_link_clickers", "Unique link clickers"],
  ["click_through_rate", "Click-through rate", null, "percent"], ["inbound_messages", "Inbound messages", "replies"],
  ["unique_replying_guests", "Unique replying guests"], ["guest_reply_rate", "Guest reply rate", null, "percent"],
  ["conversations_started", "Conversations started"],
];

const OFFER_METRICS = [
  ["links_sent", "Offer links sent"], ["link_clicks", "Link clicks", "link_clicks"], ["claims", "Claims", "offer_claims"],
  ["redemptions", "Redemptions", "offer_redemptions"], ["claim_rate", "Claim rate", null, "percent"],
  ["redemption_rate", "Redemption rate", null, "percent"], ["estimated_redemption_value", "Estimated completed outcome value", null, "money"],
];

const VALUE_METRICS = [
  ["confirmed_revenue", "Confirmed revenue", "money"],
  ["estimated_outcome_value", "Estimated outcome value", "money"],
  ["opportunity_signal_value", "Potential opportunity signal value", "money"],
  ["haylo_cost", "Haylo cost", "money"],
  ["estimated_roi_percent", "Estimated ROI", "percent"],
  ["confirmed_roi_percent", "Confirmed ROI", "percent"],
  ["cost_per_captured_lead", "Cost per captured lead", "money"],
  ["cost_per_commercial_opportunity", "Cost per commercial opportunity", "money"],
];

const LABELS = {
  strong_activation: "Strong activation",
  needs_attention: "Needs attention",
  revenue_at_risk: "Revenue at risk",
  function_enquiry: "Function enquiry",
  function_package_request: "Function package request",
  table_booking: "Table booking",
  membership: "Membership",
  event_ticket: "Event ticket",
  guest_issue: "Guest issue",
  staff_contact: "Staff contact",
  other: "Other",
};

const hasValue = (value) => value !== null && value !== undefined && value !== "";
const humanize = (value) => LABELS[value] || String(value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatNumber = (value) => {
  if (!hasValue(value) || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-AU", { maximumFractionDigits: 2 });
};

const formatMoney = (value, currency) => {
  if (!hasValue(value) || !currency || Number.isNaN(Number(value))) return "—";
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(Number(value));
  } catch (error) {
    return `${currency} ${Number(value).toFixed(2)}`;
  }
};

const availabilityText = (status) => {
  if (status === "not_configured") return "Not configured";
  if (status === "not_tracked") return "Not tracked";
  if (status === "not_applicable") return "—";
  return null;
};

const availabilityTitle = (source, key) => {
  const value = source && source[key];
  if (!value || typeof value !== "object") return undefined;
  if (value.status === "not_configured") return "This value has not been configured for the venue.";
  if (value.status === "not_tracked") return "This value is not currently tracked.";
  if (value.status === "not_applicable") return "This value is not applicable because its denominator is zero.";
  return undefined;
};

const formatMetricValue = (source, key, type, currency) => {
  const raw = source && source[key];
  const statusValue = raw && typeof raw === "object" && Object.prototype.hasOwnProperty.call(raw, "status") ? raw : null;
  if (statusValue && statusValue.status !== "available") return availabilityText(statusValue.status) || "—";
  const value = statusValue ? statusValue.value : raw;
  if (type === "money") return formatMoney(value, currency);
  if (type === "percent") return hasValue(value) && !Number.isNaN(Number(value)) ? `${formatNumber(value)}%` : "—";
  return formatNumber(value);
};

const getErrorMessage = (error, fallback) => {
  const response = error && error.response && error.response.data;
  if (response && typeof response.message === "string" && response.message.trim()) return response.message;
  if (response && response.errors) {
    const first = Object.keys(response.errors)[0];
    if (first && Array.isArray(response.errors[first])) return response.errors[first][0];
  }
  return fallback;
};

const getDefinition = (definitions, key) => definitions && definitions.metrics && definitions.metrics[key];

const InfoTip = ({ definitions, metric }) => {
  const definition = getDefinition(definitions, metric);
  if (!definition || !definition.definition) return null;
  return <span className="roi-info-tip" tabIndex="0" title={definition.definition} aria-label={definition.definition}><FiHelpCircle /></span>;
};

const Direction = ({ comparison }) => {
  if (!comparison) return <span className="roi-direction is-muted">No comparison</span>;
  if (comparison.direction === "not_tracked") return <span className="roi-direction is-muted">Not tracked</span>;
  const increased = comparison.direction === "increased";
  const decreased = comparison.direction === "decreased";
  const Icon = increased ? FiArrowUpRight : decreased ? FiArrowDownRight : FiArrowRight;
  const text = hasValue(comparison.change_percent)
    ? `${Math.abs(Number(comparison.change_percent))}% ${increased ? "increase" : decreased ? "decrease" : "no change"}`
    : humanize(comparison.direction);
  return <span className={`roi-direction ${increased ? "is-up" : decreased ? "is-down" : "is-flat"}`}><Icon /> {text}</span>;
};

class RoiDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dateFrom: "", dateTo: "", latestMonth: "", dashboard: null, definitions: null,
      loading: false, definitionsLoading: false, error: "", filterError: "",
      drillDownMetric: null, drillDownLabel: "", drillDownRecords: [],
      drillDownPagination: { ...EMPTY_PAGINATION }, drillDownLoading: false, drillDownError: "",
      drillDownCategory: null, pdfLoading: false, pdfError: "",
    };
    this.isMountedFlag = false;
    this.dashboardRequestId = 0;
    this.drillDownRequestId = 0;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadDashboard();
    this.loadDefinitions();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
  }

  loadDashboard = async (useFilters) => {
    const requestId = ++this.dashboardRequestId;
    const params = useFilters ? { date_from: this.state.dateFrom, date_to: this.state.dateTo } : undefined;
    this.setState({ loading: true, error: "", filterError: "", pdfError: "" });
    try {
      const response = await fetchRoiDashboard(params);
      const payload = response && response.data;
      const dashboard = payload && payload.data;
      if (!dashboard || !dashboard.period || payload.success === false) throw new Error("Invalid ROI dashboard response");
      if (this.isMountedFlag && requestId === this.dashboardRequestId) {
        this.setState({
          dashboard,
          dateFrom: dashboard.period.date_from || "",
          dateTo: dashboard.period.date_to || "",
          latestMonth: this.state.latestMonth || moment(dashboard.period.date_to || dashboard.period.date_from).format("YYYY-MM"),
          loading: false,
        });
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.dashboardRequestId) {
        const message = getErrorMessage(error, "The ROI report is unavailable right now.");
        this.setState((state) => ({
          loading: false,
          error: error && error.response && error.response.status === 422 ? "" : message,
          filterError: error && error.response && error.response.status === 422 ? message : state.filterError,
        }));
      }
    }
  };

  loadDefinitions = async () => {
    this.setState({ definitionsLoading: true });
    try {
      const response = await fetchRoiDefinitions();
      const payload = response && response.data;
      if (this.isMountedFlag) this.setState({ definitions: payload && payload.data, definitionsLoading: false });
    } catch (error) {
      if (this.isMountedFlag) this.setState({ definitionsLoading: false });
    }
  };

  handleDateChange = (event) => this.setState({ [event.target.name]: event.target.value, filterError: "" });

  selectMonth = (monthValue) => {
    const selectedMonth = moment(monthValue, "YYYY-MM", true);
    if (!selectedMonth.isValid()) return;
    this.setState({
      dateFrom: selectedMonth.clone().startOf("month").format("YYYY-MM-DD"),
      dateTo: selectedMonth.clone().endOf("month").format("YYYY-MM-DD"),
      filterError: "",
    }, () => this.loadDashboard(true));
  };

  handleMonthSelect = (event) => this.selectMonth(event.target.value);

  handleApplyDates = (event) => {
    event.preventDefault();
    const { dateFrom, dateTo } = this.state;
    if (!dateFrom || !dateTo) {
      this.setState({ filterError: "Choose both a start and end date." });
      return;
    }
    if (moment(dateFrom).isAfter(moment(dateTo), "day")) {
      this.setState({ filterError: "The start date must be on or before the end date." });
      return;
    }
    this.loadDashboard(true);
  };

  openDrillDown = (metric, label, category) => {
    if (!metric) return;
    this.setState({
      drillDownMetric: metric, drillDownLabel: label, drillDownCategory: category || null,
      drillDownRecords: [], drillDownPagination: { ...EMPTY_PAGINATION }, drillDownError: "",
    }, () => this.loadDrillDown(1));
  };

  closeDrillDown = () => this.setState({ drillDownMetric: null, drillDownRecords: [], drillDownError: "" });

  loadDrillDown = async (page) => {
    const { dashboard, drillDownMetric, drillDownCategory } = this.state;
    if (!dashboard || !drillDownMetric) return;
    const requestId = ++this.drillDownRequestId;
    const params = {
      date_from: dashboard.period.date_from,
      date_to: dashboard.period.date_to,
      page: page || 1,
      per_page: 20,
    };
    if (drillDownCategory) params.category = drillDownCategory;
    this.setState({ drillDownLoading: true, drillDownError: "" });
    try {
      const response = await fetchRoiDrillDown(drillDownMetric, params);
      const payload = response && response.data;
      const data = payload && payload.data;
      if (!data || !Array.isArray(data.records) || payload.success === false) throw new Error("Invalid drill-down response");
      if (this.isMountedFlag && requestId === this.drillDownRequestId) {
        this.setState({
          drillDownRecords: data.records,
          drillDownPagination: { ...EMPTY_PAGINATION, ...(data.pagination || {}) },
          drillDownLoading: false,
        });
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.drillDownRequestId) {
        this.setState({
          drillDownLoading: false,
          drillDownError: error && error.response && error.response.status === 404
            ? "Event not available."
            : getErrorMessage(error, "These records could not be loaded."),
        });
      }
    }
  };

  previewReport = async () => {
    const report = this.state.dashboard && this.state.dashboard.report;
    if (!report || !report.preview_url || this.state.pdfLoading) return;
    this.setState({ pdfLoading: true, pdfError: "" });
    try {
      const response = await previewRoiReport(report.preview_url);
      const objectUrl = window.URL.createObjectURL(response.data);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      if (this.isMountedFlag) this.setState({ pdfLoading: false });
    } catch (error) {
      if (this.isMountedFlag) this.setState({ pdfLoading: false, pdfError: "The report preview could not be generated." });
    }
  };

  downloadReport = async () => {
    const report = this.state.dashboard && this.state.dashboard.report;
    if (!report || !report.download_url || this.state.pdfLoading) return;
    this.setState({ pdfLoading: true, pdfError: "" });
    try {
      const response = await downloadRoiReport(report.download_url);
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = objectUrl;
      if (report.filename) link.download = report.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      if (this.isMountedFlag) this.setState({ pdfLoading: false });
    } catch (error) {
      if (this.isMountedFlag) this.setState({ pdfLoading: false, pdfError: "The report download could not be generated." });
    }
  };

  renderInitialState() {
    if (this.state.loading) {
      return <div className="roi-initial-state" aria-live="polite"><span className="spinner-border" role="status" /><p>Preparing your ROI report…</p></div>;
    }
    return (
      <div className="roi-initial-state is-error" role="alert">
        <FiAlertCircle /><h3>We couldn’t load this report</h3><p>{this.state.error}</p>
        <button type="button" className="roi-button roi-button-primary" onClick={() => this.loadDashboard()}>Retry</button>
      </div>
    );
  }

  renderExecutiveCards() {
    const { dashboard, definitions } = this.state;
    const summary = dashboard.executive_summary || {};
    const comparison = dashboard.comparison || {};
    return (
      <div className="roi-executive-grid">
        {EXECUTIVE_CARDS.map((card) => {
          const Icon = card.icon;
          const definitionKey = card.metric;
          return (
            <button type="button" className="roi-stat-card" key={card.key} onClick={() => this.openDrillDown(card.metric, card.label)}>
              <span className={`roi-stat-icon is-${card.tone}`}><Icon /></span>
              <span className="roi-stat-copy"><span className="roi-stat-label">{card.label} <InfoTip definitions={definitions} metric={definitionKey} /></span><strong>{formatNumber(summary[card.key])}</strong><Direction comparison={comparison[card.key]} /></span>
              <FiChevronRight className="roi-card-chevron" />
            </button>
          );
        })}
      </div>
    );
  }

  renderMetricGrid(title, icon, source, metrics, className) {
    const { dashboard, definitions } = this.state;
    const Icon = icon;
    return (
      <section className={`roi-panel ${className || ""}`}>
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><Icon /></span><div><h2>{title}</h2></div></div>
        <div className="roi-detail-grid">
          {metrics.map(([key, label, drillMetric, type]) => {
            const content = [
              <span key="label">{label} <InfoTip definitions={definitions} metric={key} /></span>,
              <strong key="value" title={availabilityTitle(source, key)}>{formatMetricValue(source, key, type, dashboard.currency)}</strong>,
            ];
            return drillMetric ? (
              <button type="button" className="roi-detail-item is-action" key={key} onClick={() => this.openDrillDown(drillMetric, label)}>{content}<FiChevronRight /></button>
            ) : <div className="roi-detail-item" key={key}>{content}</div>;
          })}
        </div>
      </section>
    );
  }

  renderCommercialIntent() {
    const { dashboard, definitions } = this.state;
    const opportunities = dashboard.opportunities || {};
    const categories = opportunities.by_category || {};
    return (
      <section className="roi-panel">
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><FiTarget /></span><div><h2>Commercial intent</h2><p>Signals identified from automation replies</p></div></div>
        <div className="roi-commercial-total"><span>Total opportunities <InfoTip definitions={definitions} metric="opportunities" /></span><strong>{formatNumber(opportunities.total)}</strong></div>
        <div className="roi-category-list">
          {Object.keys(categories).length ? Object.keys(categories).map((category) => (
            <button type="button" key={category} onClick={() => this.openDrillDown("opportunities", humanize(category), category)}>
              <span>{humanize(category)}</span><strong>{formatNumber(categories[category])}</strong><FiChevronRight />
            </button>
          )) : <p className="roi-empty-copy">No commercial opportunity categories were returned.</p>}
        </div>
        <div className="roi-commercial-outcomes">
          <div><span>Confirmed bookings</span><strong title={availabilityTitle(opportunities, "confirmed_bookings")}>{formatMetricValue(opportunities, "confirmed_bookings", null, dashboard.currency)}</strong></div>
          <div><span>Confirmed revenue</span><strong title={availabilityTitle(opportunities, "confirmed_revenue")}>{formatMetricValue(opportunities, "confirmed_revenue", "money", dashboard.currency)}</strong></div>
        </div>
        <div className="roi-signal-value"><span>Potential opportunity signal value</span><strong>{opportunities.value_status === "available" ? formatMoney(opportunities.estimated_signal_value, dashboard.currency) : availabilityText(opportunities.value_status) || "—"}</strong><small>This is an intent signal, not confirmed revenue.</small></div>
      </section>
    );
  }

  renderActivation() {
    const activation = this.state.dashboard.activation_score || {};
    const score = hasValue(activation.score) ? Number(activation.score) : null;
    const components = activation.components || {};
    return (
      <section className="roi-panel roi-activation-panel">
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><FiTrendingUp /></span><div><h2>Activation score</h2><p>{humanize(activation.label) || "Current venue activation"}</p></div></div>
        <div className="roi-score-row">
          <div className="roi-score-ring" style={{ "--roi-score": score === null ? 0 : score }} role="img" aria-label={score === null ? "Activation score unavailable" : `Activation score ${score} out of 100`}><strong>{score === null ? "—" : formatNumber(score)}</strong><span>/ 100</span></div>
          <div className="roi-score-components">
            {Object.keys(components).length ? Object.keys(components).map((key) => {
              const component = components[key];
              return (
                <div key={key}>
                  <span>{humanize(key)}</span>
                  {component && typeof component === "object" ? (
                    <span className="roi-component-value"><strong>{formatNumber(component.score)} / {formatNumber(component.weight)}</strong><small>{formatNumber(component.performance_percent)}% performance</small></span>
                  ) : <strong>{formatNumber(component)}</strong>}
                </div>
              );
            }) : <p className="roi-empty-copy">No score components were returned.</p>}
          </div>
        </div>
      </section>
    );
  }

  renderComparison() {
    const comparison = this.state.dashboard.comparison || {};
    const period = this.state.dashboard.period || {};
    const previousPeriod = period.previous_date_from && period.previous_date_to
      ? `${moment(period.previous_date_from).format("D MMM YYYY")} – ${moment(period.previous_date_to).format("D MMM YYYY")}`
      : "Previous reporting period";
    return (
      <section className="roi-panel roi-comparison-panel">
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><FiTrendingUp /></span><div><h2>Period comparison</h2><p>Compared with {previousPeriod}</p></div></div>
        <div className="roi-comparison-list">
          {Object.keys(comparison).length ? Object.keys(comparison).map((key) => {
            const item = comparison[key] || {};
            return <div className="roi-comparison-row" key={key}><span>{humanize(key)}</span><div><small>{formatNumber(item.previous)}</small><span className="roi-comparison-arrow"><FiArrowRight /></span><strong>{formatNumber(item.current)}</strong></div><Direction comparison={item} /></div>;
          }) : <p className="roi-empty-copy">No comparison data was returned.</p>}
        </div>
      </section>
    );
  }

  renderTopEvents() {
    const events = Array.isArray(this.state.dashboard.top_events) ? this.state.dashboard.top_events.slice(0, 5) : [];
    const currency = this.state.dashboard.currency;
    return (
      <section className="roi-panel">
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><FiFlag /></span><div><h2>Top performing events</h2><p>Ranked by the venue reporting service</p></div></div>
        {events.length ? <div className="roi-table-wrap"><table className="roi-table roi-events-table"><thead><tr><th>Event</th><th>Date</th><th>Captures</th><th>Unique leads</th><th>Follow-ups</th><th>Replies</th><th>Opportunities</th><th>Automatic conversions</th><th>Estimated outcome</th><th>Confirmed revenue</th></tr></thead><tbody>{events.map((event, index) => <tr key={event.id || event.event_id || index}><td><strong>{event.name || event.event_name || "Event"}</strong><small className="roi-event-id">Event #{event.id || event.event_id || "—"}</small></td><td>{event.date || event.event_date ? moment(event.date || event.event_date).format("D MMM YYYY") : "—"}</td><td>{formatNumber(event.media_captures)}</td><td>{formatNumber(event.unique_leads)}</td><td>{formatNumber(event.follow_ups_sent)}</td><td>{formatNumber(hasValue(event.replies) ? event.replies : event.unique_replying_guests)}</td><td>{formatNumber(hasValue(event.opportunities) ? event.opportunities : event.commercial_opportunities)}</td><td>{formatNumber(event.automatic_conversions)}</td><td>{formatMoney(event.estimated_outcome_value, currency)}</td><td title={availabilityTitle(event, "confirmed_revenue")}>{formatMetricValue(event, "confirmed_revenue", "money", currency)}</td></tr>)}</tbody></table></div> : <p className="roi-empty-copy">No top events were returned for this period.</p>}
      </section>
    );
  }

  renderServerItems(title, icon, source, emptyText) {
    const Icon = icon;
    let items = [];
    if (Array.isArray(source)) items = source.filter((item) => item !== null && item !== undefined);
    else if (source && typeof source === "object") {
      items = Object.keys(source).filter((key) => source[key] !== null && source[key] !== undefined).map((key) => {
        const value = source[key];
        if (value && typeof value === "object") return { ...value, __sourceKey: key };
        return { count: value, __sourceKey: key };
      });
    }
    return (
      <section className="roi-panel">
        <div className="roi-panel-heading"><span className="roi-panel-heading-icon"><Icon /></span><div><h2>{title}</h2></div></div>
        <div className="roi-action-list">
          {items.length ? items.map((item, index) => {
            const itemObject = item && typeof item === "object" ? item : {};
            const titleText = typeof item === "string" ? item : itemObject.recommendation || itemObject.title || itemObject.label || itemObject.heading || itemObject.wording || itemObject.message || humanize(itemObject.type || itemObject.__sourceKey);
            const recommendationMeta = [];
            if (hasValue(itemObject.priority)) recommendationMeta.push(`Priority ${itemObject.priority}`);
            if (itemObject.key) recommendationMeta.push(humanize(itemObject.key));
            if (hasValue(itemObject.score_gap)) recommendationMeta.push(`Score gap ${formatNumber(itemObject.score_gap)}`);
            const detail = itemObject.description || itemObject.detail || itemObject.body || itemObject.reason || recommendationMeta.join(" · ");
            const count = itemObject.count;
            const countText = typeof count === "string" ? humanize(count) : formatNumber(count);
            return <div className="roi-action-item" key={itemObject.id || itemObject.key || index}><span className="roi-action-bullet"><FiArrowUpRight /></span><div><strong>{titleText || "Action"}{hasValue(count) ? ` (${countText})` : ""}</strong>{detail ? <small>{detail}</small> : null}</div></div>;
          }) : <p className="roi-empty-copy">{emptyText}</p>}
        </div>
      </section>
    );
  }

  renderAvailabilityNotice() {
    const availability = this.state.dashboard.availability || {};
    const availabilityKeys = Object.keys(availability);
    if (!availabilityKeys.length) return null;
    return (
      <div className="roi-availability-note"><FiAlertCircle /><div><strong>Data availability</strong><p>Historical metrics are available only from their tracking deployment onward.</p><div className="roi-availability-list">{availabilityKeys.map((key) => <span key={key}><b>{humanize(key)}</b>{humanize(availability[key])}</span>)}</div></div></div>
    );
  }

  renderDrillDown() {
    const { drillDownMetric, drillDownLabel, drillDownRecords, drillDownPagination, drillDownLoading, drillDownError } = this.state;
    if (!drillDownMetric) return null;
    const columns = drillDownRecords.length ? Object.keys(drillDownRecords[0]).filter((key) => typeof drillDownRecords[0][key] !== "object") : [];
    return (
      <div className="roi-modal-backdrop" role="presentation" onMouseDown={this.closeDrillDown}>
        <div className="roi-modal" role="dialog" aria-modal="true" aria-labelledby="roi-drill-title" onKeyDown={(event) => { if (event.key === "Escape") this.closeDrillDown(); }} onMouseDown={(event) => event.stopPropagation()}>
          <div className="roi-modal-header"><div><span>Report detail</span><h2 id="roi-drill-title">{drillDownLabel}</h2></div><button type="button" onClick={this.closeDrillDown} aria-label="Close details"><FiX /></button></div>
          <div className="roi-modal-body" aria-live="polite">
            {drillDownLoading ? <div className="roi-modal-loading"><span className="spinner-border spinner-border-sm" /> Loading records…</div> : null}
            {drillDownError ? <div className="roi-inline-error"><span>{drillDownError}</span><button type="button" onClick={() => this.loadDrillDown(drillDownPagination.current_page)}>Retry</button></div> : null}
            {!drillDownLoading && !drillDownError && !drillDownRecords.length ? <p className="roi-empty-copy">No records were returned for this period.</p> : null}
            {drillDownRecords.length ? <div className="roi-table-wrap"><table className="roi-table"><thead><tr>{columns.map((key) => <th key={key}>{humanize(key)}</th>)}</tr></thead><tbody>{drillDownRecords.map((record, index) => <tr key={record.id || index}>{columns.map((key) => <td key={key}>{typeof record[key] === "boolean" ? (record[key] ? "Yes" : "No") : hasValue(record[key]) ? String(record[key]) : "—"}</td>)}</tr>)}</tbody></table></div> : null}
          </div>
          <div className="roi-modal-footer"><span>{drillDownPagination.total ? `${formatNumber(drillDownPagination.from)}–${formatNumber(drillDownPagination.to)} of ${formatNumber(drillDownPagination.total)}` : "0 records"}</span><div><button type="button" disabled={drillDownLoading || drillDownPagination.current_page <= 1} onClick={() => this.loadDrillDown(drillDownPagination.current_page - 1)} aria-label="Previous page"><FiChevronLeft /></button><span>Page {drillDownPagination.current_page} of {drillDownPagination.last_page}</span><button type="button" disabled={drillDownLoading || drillDownPagination.current_page >= drillDownPagination.last_page} onClick={() => this.loadDrillDown(drillDownPagination.current_page + 1)} aria-label="Next page"><FiChevronRight /></button></div></div>
        </div>
      </div>
    );
  }

  renderMonthRail() {
    const { dashboard, latestMonth, loading } = this.state;
    const period = dashboard.period || {};
    const anchorMonth = moment(latestMonth, "YYYY-MM", true);
    const safeAnchor = anchorMonth.isValid() ? anchorMonth.clone().startOf("month") : moment().startOf("month");
    const months = [];
    for (let index = 0; index < 12; index += 1) months.push(safeAnchor.clone().subtract(index, "month"));
    const selectedMonth = moment(period.date_from).isValid() ? moment(period.date_from).format("YYYY-MM") : safeAnchor.format("YYYY-MM");

    return (
      <aside className="roi-month-rail" aria-label="Monthly report periods">
        <div className="roi-month-select-wrap">
          <FiCalendar />
          <select value={selectedMonth} onChange={this.handleMonthSelect} disabled={loading} aria-label="Select report month">
            {months.map((month) => <option value={month.format("YYYY-MM")} key={month.format("YYYY-MM")}>{month.format("MMMM YYYY")}</option>)}
          </select>
        </div>
        <div className="roi-month-list">
          <div className="roi-month-list-heading"><h2>Available Reports</h2><span>Monthly periods</span></div>
          {months.map((month, index) => {
            const monthValue = month.format("YYYY-MM");
            const active = selectedMonth === monthValue;
            return (
              <button type="button" className={`roi-month-item ${active ? "is-active" : ""}`} key={monthValue} disabled={loading} onClick={() => this.selectMonth(monthValue)} aria-current={active ? "true" : undefined}>
                <span className="roi-month-item-icon"><FiFileText /></span>
                <span className="roi-month-item-copy"><strong>{month.format("MMMM YYYY")}</strong><small>{month.clone().startOf("month").format("D MMM")} – {month.clone().endOf("month").format("D MMM YYYY")}</small></span>
                {index === 0 ? <span className="roi-latest-badge">Latest</span> : null}
              </button>
            );
          })}
        </div>
      </aside>
    );
  }

  renderDashboard() {
    const { dashboard, loading, error, filterError, pdfLoading, pdfError, definitionsLoading } = this.state;
    const report = dashboard.report || {};
    const period = dashboard.period || {};
    return (
      <div className={`roi-dashboard ${loading ? "is-refreshing" : ""}`}>
        <div className="roi-page-title"><div><span className="roi-eyebrow">Venue performance</span><h1>ROI Reports</h1><p>Clear proof of engagement, commercial intent and measurable outcomes.</p></div><div className="roi-report-period"><FiCalendar /><span><small>Reporting period</small><strong>{report.period_label || `${period.date_from || ""} – ${period.date_to || ""}`}</strong></span></div></div>

        <div className="roi-workspace">
          {this.renderMonthRail()}
          <main className="roi-report-content">

        <form className="roi-filter-bar" onSubmit={this.handleApplyDates}>
          <label><span>From</span><input type="date" name="dateFrom" value={this.state.dateFrom} onChange={this.handleDateChange} /></label>
          <label><span>To</span><input type="date" name="dateTo" value={this.state.dateTo} onChange={this.handleDateChange} /></label>
          <button type="submit" className="roi-button roi-button-primary" disabled={loading}>{loading ? "Refreshing…" : "Update report"}</button>
          <span className="roi-filter-meta">Venue timezone: {period.timezone || "—"} · Currency: {dashboard.currency || "—"}</span>
          {filterError ? <div className="roi-filter-error" role="alert">{filterError}</div> : null}
        </form>

        {error ? <div className="roi-inline-error" role="alert"><span>{error}</span><button type="button" onClick={() => this.loadDashboard(true)}>Retry</button></div> : null}

        <section className="roi-report-hero">
          <div className="roi-hero-copy"><span className="roi-featured-label">Featured report</span><h2>{report.title || "Venue Performance Report"}</h2><p><FiCalendar /> {period.date_from ? `${moment(period.date_from).format("D MMM YYYY")} – ${moment(period.date_to).format("D MMM YYYY")}` : "Current reporting period"}</p><small>Your venue’s performance, engagement, commercial intent and outcome insights in one report.</small><div className="roi-pdf-actions"><button type="button" className="roi-button roi-button-secondary" disabled={pdfLoading || !report.preview_url} onClick={this.previewReport}><FiFileText /> {pdfLoading ? "Preparing…" : "Preview PDF"}</button><button type="button" className="roi-button roi-button-primary" disabled={pdfLoading || !report.download_url} onClick={this.downloadReport}><FiDownload /> {pdfLoading ? "Preparing…" : "Download PDF"}</button></div>{report.filename ? <span className="roi-report-filename">PDF: {report.filename}</span> : null}{pdfError ? <p className="roi-pdf-error" role="alert">{pdfError}</p> : null}</div>
          <div className="roi-report-preview" aria-hidden="true"><div className="roi-preview-top"><span>the haylo</span><small>{report.period_label}</small></div><h3>{report.title}</h3><p>Venue performance report</p><i /><i /><i /><i /><div className="roi-preview-chart"><b /><b /><b /><b /><b /></div></div>
          <div className="roi-hero-insights"><h3>Report highlights</h3><div><span className="is-green"><FiTrendingUp /></span><p><strong>{formatNumber((dashboard.activation_score || {}).score)} / 100</strong><small>{humanize((dashboard.activation_score || {}).label)}</small></p></div><div><span className="is-gold"><FiTarget /></span><p><strong>{formatNumber((dashboard.opportunities || {}).total)}</strong><small>Commercial opportunities</small></p></div><div><span className="is-blue"><FiMessageCircle /></span><p><strong>{formatNumber((dashboard.follow_up || {}).unique_replying_guests)}</strong><small>Unique replying guests</small></p></div></div>
        </section>

        {this.renderExecutiveCards()}

        <div className="roi-primary-grid">
          {this.renderActivation()}
          {this.renderMetricGrid("Value summary", FiTrendingUp, dashboard.value_summary || {}, VALUE_METRICS, "roi-value-panel")}
        </div>

        {this.renderAvailabilityNotice()}

        <div className="roi-section-grid">
          {this.renderMetricGrid("Guest capture", FiUsers, dashboard.guest_capture || {}, GUEST_METRICS)}
          {this.renderMetricGrid("Follow-up performance", FiSend, dashboard.follow_up || {}, FOLLOW_UP_METRICS)}
          {this.renderCommercialIntent()}
          {this.renderMetricGrid("Offers & redemptions", FiCheckCircle, {
            ...(dashboard.offers || {}),
            estimated_redemption_value: {
              value: (dashboard.offers || {}).estimated_redemption_value,
              status: (dashboard.offers || {}).value_status || "not_tracked",
            },
          }, OFFER_METRICS)}
        </div>

        <div className="roi-lower-grid">
          {this.renderTopEvents()}
          {this.renderComparison()}
        </div>

        <div className="roi-lower-grid">
          {this.renderServerItems("Recommended actions", FiTarget, dashboard.recommendations, "No recommended actions for this period.")}
          {this.renderServerItems("Needs attention", FiAlertCircle, dashboard.attention, "Nothing needs attention right now.")}
        </div>
        {definitionsLoading ? <p className="roi-definitions-loading" aria-live="polite">Loading metric definitions…</p> : null}
          </main>
        </div>
      </div>
    );
  }

  render() {
    return <Layout>{this.state.dashboard ? this.renderDashboard() : this.renderInitialState()}{this.renderDrillDown()}</Layout>;
  }
}

export default RoiDashboard;
